// Zod validation schemas for Hotelia data models
import { z } from 'zod';

export const visibilitySchema = z.enum([
  'public',
  'invite_only',
  'invite_or_apply',
  'group_only',
  'event_only',
  'friends_of_host',
]);

export const roleSchema = z.enum([
  'owner',
  'cohost',
  'member',
  'sponsored_member',
]);

export const rsvpStateSchema = z.enum([
  'interested',
  'going',
  'checked_in',
  'cancelled',
]);

export const inviteStatusSchema = z.enum([
  'sent',
  'accepted',
  'declined',
  'expired',
]);

export const profileVisibilitySchema = z.enum([
  'non_discoverable',
  'discoverable_by_link_only',
]);

export const attendeesVisibleSchema = z.enum([
  'off',
  'mutuals_only',
  'friends_only',
]);

export const hostTypeSchema = z.enum(['profile', 'group', 'event']);

// Phone number validation (E.164 format)
export const phoneSchema = z.string().regex(/^\+[1-9]\d{1,14}$/, {
  message: 'Phone must be in E.164 format (e.g., +15551234567)',
});

// Group Rules Schema
export const groupRulesSchema = z.object({
  membersCanInvite: z.boolean(),
  membersCanCreateEvents: z.boolean().optional(),
  groupRulesText: z.string().max(1000).optional(),
  maxSponsorsPerMember: z.number().int().min(1).max(10).optional(),
  sponsorEligibility: z
    .object({
      minAttended: z.number().nonnegative().optional(),
      requireOneEvent: z.boolean().optional(),
    })
    .optional(),
  sponsorAccountabilityEnabled: z.boolean(),
  sponsorAccountabilityText: z.string().max(500).optional(),
});

// Group Chat Schema
export const groupChatSchema = z.object({
  enabled: z.boolean(),
});

// Group Schema - use nullish() to handle undefined/null, then transform to remove them
export const groupSchema = z.object({
  ownerId: z.string(),
  parentType: hostTypeSchema,
  parentId: z.string(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).nullish(),
  website: z.string().url().max(500).nullish(),
  image: z.string().url().nullish(),
  visibility: z.enum(['public', 'invite_only', 'invite_or_apply']),
  allowSubgroups: z.boolean(),
  rules: groupRulesSchema,
  groupChat: groupChatSchema.optional(),
}).transform((data) => {
  // Remove undefined, null, and empty string values
  // BUT preserve boolean false values and objects with boolean values
  const cleaned: any = {};
  Object.keys(data).forEach((key) => {
    const value = (data as any)[key];
    // Skip undefined, null, and empty strings, but keep boolean false/true
    if (value !== undefined && value !== null && value !== '' || value === false || value === true) {
      if (typeof value === 'object' && !Array.isArray(value) && value !== null && !(value instanceof Date)) {
        // Clean nested objects, preserving boolean false/true
        const cleanedNested: any = {};
        Object.keys(value).forEach((nestedKey) => {
          const nestedValue = (value as any)[nestedKey];
          // Keep boolean false/true, but remove undefined, null, and empty strings
          if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '' || nestedValue === false || nestedValue === true) {
            cleanedNested[nestedKey] = nestedValue;
          }
        });
        // Only add nested object if it has at least one key
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key] = cleanedNested;
        }
      } else {
        cleaned[key] = value;
      }
    }
  });
  return cleaned;
});

// Group Member Schema
export const groupMemberSchema = z.object({
  userId: z.string(),
  groupId: z.string(),
  role: roleSchema,
  sponsoredBy: z.string().optional(),
  joinedAt: z.date(),
  suspended: z
    .object({
      by: z.string(),
      reason: z.string(),
      at: z.date(),
    })
    .optional(),
});

// Event Rules Schema
export const eventRulesSchema = z.object({
  attendeesVisible: attendeesVisibleSchema,
  membersCanInvite: z.boolean(),
  bringAGuest: z.number().int().min(0).max(10),
  sponsorshipAtDoor: z.boolean(),
});

// Event Location Schema
export const eventLocationSchema = z.object({
  text: z.string().min(1),
  lat: z.number().optional(),
  lng: z.number().optional(),
});

// Event Schema
export const eventSchema = z.object({
  hostType: hostTypeSchema,
  hostId: z.string(),
  title: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  startsAt: z.date(),
  endsAt: z.date().optional(),
  location: eventLocationSchema,
  capacity: z.number().positive().optional(),
  visibility: visibilitySchema,
  rules: eventRulesSchema,
  website: z.string().url().optional().or(z.literal('')),
  groupChat: groupChatSchema.optional(),
  qrMode: z
    .object({
      rotateSeconds: z.number().positive(),
    })
    .optional(),
});

// Event Invite Schema
export const eventInviteSchema = z.object({
  eventId: z.string(),
  inviterId: z.string(),
  targetPhone: phoneSchema.optional(),
  targetUserId: z.string().optional(),
  role: z.enum(['guest', 'member']),
  status: inviteStatusSchema,
});

// Event RSVP Schema
export const eventRSVPSchema = z.object({
  userId: z.string(),
  eventId: z.string(),
  state: rsvpStateSchema,
  plusOnes: z.number().int().min(0).max(10),
  checkedInAt: z.date().optional(),
  checkInMethod: z.enum(['qr', 'manual']).optional(),
});

// Invite Token Schema
export const inviteTokenSchema = z.object({
  target: z.object({
    kind: z.enum(['profile_follow', 'group_join', 'event_join']),
    id: z.string(),
  }),
  issuedBy: z.string(),
  expiresAt: z.date(),
  oneTime: z.boolean(),
  metadata: z
    .object({
      sponsorId: z.string().optional(),
      role: z.enum(['guest', 'member']).optional(),
    })
    .optional(),
});

// User Profile Schema
export const userProfileSchema = z.object({
  name: z.string().min(1).max(100),
  phone: phoneSchema,
  email: z.string().email().optional(),
  photoURL: z.string().url().optional(),
  profileVisibility: profileVisibilitySchema,
  allowFollowRequests: z.boolean(),
});

// Follow Schema
export const followSchema = z.object({
  userId: z.string(),
  targetUserId: z.string(),
  status: z.enum(['pending', 'approved', 'blocked']),
});


